import { useCallback, useEffect, useRef, useState } from "react";
import { ImageBlockStatus, useGetImageGenerationJobStatusLazyQuery } from "../graphql/generated/graphql";
import type { MessageItem } from "../containers/companion/companionMessage.types";

type ImagePollTarget = {
  streamChatKey: string;
  messageId: string;
};

type SetMessagesByChatId = React.Dispatch<React.SetStateAction<Record<string, MessageItem[]>>>;

export function useImageGenerationPolling(setMessagesByChatId: SetMessagesByChatId) {
  const [fetchImageGenerationJobStatus] = useGetImageGenerationJobStatusLazyQuery();
  const imagePollTargetsRef = useRef<Record<string, ImagePollTarget>>({});
  const [imagePollVersion, setImagePollVersion] = useState(0);

  const stopImageGenerationPolling = useCallback((jobId?: string) => {
    if (jobId) {
      if (!imagePollTargetsRef.current[jobId]) return;
      const rest = { ...imagePollTargetsRef.current };
      delete rest[jobId];
      imagePollTargetsRef.current = rest;
      setImagePollVersion((prev) => prev + 1);
      return;
    }
    if (Object.keys(imagePollTargetsRef.current).length === 0) return;
    imagePollTargetsRef.current = {};
    setImagePollVersion((prev) => prev + 1);
  }, []);

  const stopImageGenerationPollingForMessage = useCallback((messageId: string) => {
    const entries = Object.entries(imagePollTargetsRef.current).filter(([, target]) => target.messageId !== messageId);
    if (entries.length === Object.keys(imagePollTargetsRef.current).length) return;
    imagePollTargetsRef.current = Object.fromEntries(entries);
    setImagePollVersion((prev) => prev + 1);
  }, []);

  const startImageGenerationPolling = useCallback((jobId: string, streamChatKey: string, aiMessageId: string) => {
    imagePollTargetsRef.current = {
      ...imagePollTargetsRef.current,
      [jobId]: { streamChatKey, messageId: aiMessageId },
    };
    setImagePollVersion((prev) => prev + 1);
  }, []);

  const remapImagePollMessageId = useCallback((oldMessageId: string, newMessageId: string) => {
    const nextTargets: Record<string, ImagePollTarget> = {};
    let changed = false;
    for (const [jobId, target] of Object.entries(imagePollTargetsRef.current)) {
      if (target.messageId === oldMessageId) {
        nextTargets[jobId] = { ...target, messageId: newMessageId };
        changed = true;
      } else {
        nextTargets[jobId] = target;
      }
    }
    if (changed) {
      imagePollTargetsRef.current = nextTargets;
      setImagePollVersion((prev) => prev + 1);
    }
  }, []);

  const remapImagePollChatKey = useCallback((oldChatKey: string, newChatKey: string) => {
    const nextTargets: Record<string, ImagePollTarget> = {};
    let changed = false;
    for (const [jobId, target] of Object.entries(imagePollTargetsRef.current)) {
      if (target.streamChatKey === oldChatKey) {
        nextTargets[jobId] = { ...target, streamChatKey: newChatKey };
        changed = true;
      } else {
        nextTargets[jobId] = target;
      }
    }
    if (changed) {
      imagePollTargetsRef.current = nextTargets;
      setImagePollVersion((prev) => prev + 1);
    }
  }, []);

  useEffect(() => {
    const withUpdatedImageBlock = (
      m: MessageItem,
      jobId: string,
      next: { status: ImageBlockStatus; url?: string | null; errorMessage?: string | null }
    ) => {
      const existing = m.imageBlocks?.[0];
      return {
        ...m,
        imageBlocks: [
          {
            id: existing?.id ?? `temp-image-block-${jobId}`,
            status: next.status,
            jobId,
            url: next.url ?? null,
            errorMessage: next.errorMessage ?? null,
          },
        ],
      };
    };

    const setImageBlockState = (
      target: ImagePollTarget,
      jobId: string,
      next: { status: ImageBlockStatus; url?: string | null; errorMessage?: string | null }
    ) => {
      setMessagesByChatId((prev) => ({
        ...prev,
        [target.streamChatKey]: (prev[target.streamChatKey] ?? []).map((m) =>
          m.id === target.messageId ? withUpdatedImageBlock(m, jobId, next) : m
        ),
      }));
    };

    const pollJob = async (jobId: string, target: ImagePollTarget) => {
      try {
        const { data } = await fetchImageGenerationJobStatus({
          variables: { jobId },
          fetchPolicy: "network-only",
        });
        if (!imagePollTargetsRef.current[jobId]) return;
        const payload = data?.getImageGenerationJobStatus;
        if (!payload) return;

        const status = payload.status;
        if (status === "in_progress") return;

        if (status === "completed") {
          const imageUrl = payload.result?.imageUrl;
          if (!imageUrl) {
            setImageBlockState(target, jobId, { status: ImageBlockStatus.Failed, errorMessage: null });
            stopImageGenerationPolling(jobId);
            return;
          }
          setImageBlockState(target, jobId, { status: ImageBlockStatus.Completed, url: imageUrl, errorMessage: null });
          stopImageGenerationPolling(jobId);
          return;
        }

        if (status === "failed" || status === "error" || status === "not_found" || status === "forbidden") {
          console.error("[useImageGenerationPolling] Image generation job failed", {
            jobId,
            status,
            message: payload.message,
          });
          setImageBlockState(target, jobId, { status: ImageBlockStatus.Failed, errorMessage: null });
          stopImageGenerationPolling(jobId);
          return;
        }

        console.error("[useImageGenerationPolling] Unexpected image generation job status", { jobId, status });
        setImageBlockState(target, jobId, { status: ImageBlockStatus.Failed, errorMessage: null });
        stopImageGenerationPolling(jobId);
      } catch (error) {
        console.error("[useImageGenerationPolling] Error polling image generation job", { jobId, error });
        setImageBlockState(target, jobId, { status: ImageBlockStatus.Failed, errorMessage: null });
        stopImageGenerationPolling(jobId);
      }
    };

    const pollAllJobs = () => {
      for (const [jobId, target] of Object.entries(imagePollTargetsRef.current)) {
        void pollJob(jobId, target);
      }
    };

    if (Object.keys(imagePollTargetsRef.current).length === 0) return;
    pollAllJobs();
    const interval = setInterval(pollAllJobs, 3000);
    return () => clearInterval(interval);
  }, [fetchImageGenerationJobStatus, imagePollVersion, stopImageGenerationPolling, setMessagesByChatId]);

  return {
    startImageGenerationPolling,
    stopImageGenerationPolling,
    stopImageGenerationPollingForMessage,
    remapImagePollMessageId,
    remapImagePollChatKey,
  };
}
